import { type ReactNode, useCallback } from "react";
import { LuCircle, LuCircleCheck as LuCheckCircle } from "react-icons/lu";
import { RiSortAsc, RiSortDesc } from "react-icons/ri";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { NumericSort, SortVariant } from "@/contexts/filters-sort-context";
import { SortType, TimePeriodLabels } from "@/contexts/filters-sort-context";

import { TimePeriod } from "../app/discover-sort-constants";

type SortWrapperProps<T> = {
  label: string;
  name: T;
  icon: ReactNode;
  isActive: boolean;
  currentSortValue: "asc" | "desc";
  showSortIcon?: boolean;
  onClick: () => void;
};

type SortData<T> = {
  setCurrentSort: (sort: SortVariant<T> | null) => void;
  currentSort: SortVariant<T> | null;
  isSortActive: (name: T) => boolean;
};

export const SortsRecordsSet = ({
  name,
  children,
}: {
  name: string;
  children: ReactNode;
}) => {
  return (
    <div className="flex flex-col">
      <div
        className="
        border-b border-border-light pb-2 font-semibold text-muted
      "
      >
        {name}
      </div>
      {children}
    </div>
  );
};

export const SortWrapper = <T extends string>({
  label,
  icon,
  currentSortValue,
  onClick,
  isActive,
  showSortIcon,
}: SortWrapperProps<T>) => {
  return (
    <div
      className="my-3 flex w-full cursor-pointer items-center justify-between"
      onClick={onClick}
    >
      <div className="flex items-center gap-4 text-sm">
        <span className="text-xl text-muted">{icon}</span>
        {label}
      </div>
      <div className="flex">
        {showSortIcon && (
          <div className="pr-2">
            {currentSortValue === "asc" ? <RiSortAsc /> : <RiSortDesc />}
          </div>
        )}
        {!isActive && currentSortValue && (
          <div className="pr-2">
            <LuCircle
              className="
                text-xl text-muted opacity-50
                hover:text-muted-foreground
              "
            />
          </div>
        )}
        {isActive && (
          <div className="pr-2">
            <LuCheckCircle className="text-xl text-green-500" />
          </div>
        )}
      </div>
    </div>
  );
};

export const BasicSortRecord = <T extends string = string>({
  label,
  name,
  icon,
  setCurrentSort,
  currentSort,
  isSortActive,
  showSortIcon = true,
}: {
  label: string;
  name: T;
  placeholder?: string;
  showSortIcon?: boolean;
  icon: ReactNode;
} & SortData<T>) => {
  const isActive = isSortActive(name);
  const currentSortValue = currentSort?.direction || "asc";

  const onClick = useCallback(() => {
    setCurrentSort({
      name,
      type: SortType.basic,
      direction: isActive && currentSortValue === "asc" ? "desc" : "asc",
    });
  }, [currentSortValue, isActive, name, setCurrentSort]);

  return (
    <div
      className="
        border-b border-border-light
        last:border-none
      "
    >
      <SortWrapper
        {...{
          label,
          name,
          icon,
          onClick,
          isActive,
          showSortIcon,
          currentSortValue: isActive ? currentSortValue : "asc",
        }}
      />
    </div>
  );
};

export const NumericWithTimeSortRecord = <T extends string = string>({
  label,
  name,
  icon,
  setCurrentSort,
  currentSort,
  isSortActive,
}: {
  label: string;
  name: T;
  placeholder?: string;
  icon: ReactNode;
} & SortData<T>) => {
  const isActive = isSortActive(name);

  const currentSortValue = currentSort?.direction || "asc";

  const onSelectRangeChange = useCallback(
    (timePeriod: TimePeriod) => {
      setCurrentSort({
        name,
        type: SortType.numericWithTime,
        timePeriod,
      } as NumericSort<T>);
    },
    [name, setCurrentSort],
  );

  const onClick = useCallback(() => {
    setCurrentSort({
      ...currentSort,
      timePeriod:
        (currentSort as NumericSort<T>)?.timePeriod || TimePeriod.AllTime,
      name,
      type: SortType.numericWithTime,
      direction: isActive && currentSortValue === "asc" ? "desc" : "asc",
    } as NumericSort<T>);
  }, [currentSort, currentSortValue, isActive, name, setCurrentSort]);

  return (
    <div className="flex flex-row justify-between border-b border-border-light">
      <SortWrapper
        {...{
          label,
          name,
          icon,
          onClick,
          isActive,
          currentSortValue: isActive ? currentSortValue : "asc",
        }}
      />
      <div className="my-2 ml-1 overflow-visible rounded-lg">
        <div className="flex flex-row gap-4">
          <Select
            onValueChange={onSelectRangeChange}
            defaultValue={
              (currentSort as NumericSort<T>)?.timePeriod || TimePeriod.AllTime
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a deposit currency" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TimePeriodLabels).map(([key, label]) => (
                <SelectItem value={key} key={key} className="cursor-pointer">
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};
